/**
 * The settings registry: what one viewer may open, for an account or a
 * location, grouped for the section list.
 */
import { ACCOUNT_SECTIONS } from './accountSections';
import { LOCATION_SECTIONS } from './locationSections';
import {
  SETTINGS_GROUPS,
  type SettingsAccess,
  type SettingsGroup,
  type SettingsScopeKind,
  type SettingsSectionDef,
  type VisibleSettingsSection,
} from './types';

export * from './types';
export { ACCOUNT_SECTIONS } from './accountSections';
export { LOCATION_SECTIONS } from './locationSections';
export { resolveAccountSettingsLink, resolveLocationSettingsLink, type SettingsDestination, type SettingsLinkInput } from './settingsLinks';

export const sectionsFor = (scope: SettingsScopeKind): readonly SettingsSectionDef[] =>
  scope === 'account' ? ACCOUNT_SECTIONS : LOCATION_SECTIONS;

/** The sections (and their tabs) this viewer may open, in display order. */
export function visibleSections(scope: SettingsScopeKind, access: SettingsAccess): VisibleSettingsSection[] {
  return sectionsFor(scope)
    .filter((s) => (s.visible ? s.visible(access) : true))
    .map(({ visible: _visible, tabs, ...rest }) => ({
      ...rest,
      tabs: (tabs ?? [])
        .filter((t) => (t.visible ? t.visible(access) : true))
        .map((t) => ({ id: t.id, label: t.label, locked: t.locked ? t.locked(access) : false, keywords: t.keywords })),
    }));
}

/** The visible sections grouped for the section list; empty groups are left out. */
export function groupedSections(sections: VisibleSettingsSection[]): Array<{ group: SettingsGroup; sections: VisibleSettingsSection[] }> {
  return SETTINGS_GROUPS.map((group) => ({ group, sections: sections.filter((s) => s.group === group) })).filter(
    (g) => g.sections.length > 0
  );
}
